import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Plus, Search, Trash2, Eye, Package, Filter,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ESTADO_COLORS = {
  activo: 'bg-emerald-100 text-emerald-800',
  vendido: 'bg-blue-100 text-blue-800',
  reparacion: 'bg-amber-100 text-amber-800',
};

const ESTADO_LABELS = {
  activo: 'Activo',
  vendido: 'Vendido',
  reparacion: 'En reparación',
};

export default function Accesorios() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('all');
  const [tipoFilter, setTipoFilter] = useState('all');
  const [tipos, setTipos] = useState([]);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [newItem, setNewItem] = useState({
    nombre: '', tipo: '', numero_serie: '', numero_factura: '',
    fecha_compra: '', año_modelo: '', precio_compra: '', estado: 'activo', notas: '',
  });

  const fetchItems = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (estadoFilter !== 'all') params.set('estado', estadoFilter);
      if (tipoFilter !== 'all') params.set('tipo', tipoFilter);
      const res = await axios.get(`${API}/accesorios?${params}`);
      setItems(res.data);
    } catch {
      toast.error('Error al cargar accesorios');
    } finally {
      setLoading(false);
    }
  }, [search, estadoFilter, tipoFilter]);

  const fetchTipos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/accesorios/tipos`);
      setTipos(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { fetchTipos(); }, [fetchTipos]);

  const handleCreate = async () => {
    if (!newItem.nombre.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    try {
      const payload = { ...newItem };
      if (payload.precio_compra) payload.precio_compra = parseFloat(payload.precio_compra);
      if (payload.año_modelo) payload.año_modelo = parseInt(payload.año_modelo);
      await axios.post(`${API}/accesorios`, payload);
      toast.success('Accesorio creado');
      setIsAddOpen(false);
      setNewItem({
        nombre: '', tipo: '', numero_serie: '', numero_factura: '',
        fecha_compra: '', año_modelo: '', precio_compra: '', estado: 'activo', notas: '',
      });
      fetchItems();
      fetchTipos();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear accesorio');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/accesorios/${deleteId}`);
      toast.success('Accesorio eliminado');
      setDeleteId(null);
      fetchItems();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const stats = {
    total: items.length,
    activos: items.filter(i => i.estado === 'activo').length,
    vendidos: items.filter(i => i.estado === 'vendido').length,
    reparacion: items.filter(i => i.estado === 'reparacion').length,
  };

  return (
    <div className="space-y-6" data-testid="accesorios-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Barlow_Condensed']">
            Accesorios
          </h1>
          <p className="text-sm text-slate-500 mt-1">Gestión de accesorios individuales</p>
        </div>
        {isAdmin && (
          <Button
            onClick={() => setIsAddOpen(true)}
            className="bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="add-accesorio-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Nuevo Accesorio
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total', value: stats.total, color: 'text-slate-900' },
          { label: 'Activos', value: stats.activos, color: 'text-emerald-600' },
          { label: 'Vendidos', value: stats.vendidos, color: 'text-blue-600' },
          { label: 'En reparación', value: stats.reparacion, color: 'text-amber-600' },
        ].map(s => (
          <Card key={s.label}>
            <CardContent className="p-4 text-center">
              <p className="text-xs text-slate-500 uppercase tracking-wide">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por nombre, código, tipo..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-10"
                data-testid="accesorios-search"
              />
            </div>
            <Select value={estadoFilter} onValueChange={setEstadoFilter}>
              <SelectTrigger className="w-full sm:w-[160px]" data-testid="estado-filter">
                <Filter className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="activo">Activo</SelectItem>
                <SelectItem value="vendido">Vendido</SelectItem>
                <SelectItem value="reparacion">En reparación</SelectItem>
              </SelectContent>
            </Select>
            <Select value={tipoFilter} onValueChange={setTipoFilter}>
              <SelectTrigger className="w-full sm:w-[160px]" data-testid="tipo-filter">
                <Filter className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                {tipos.map(t => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-slate-500">Cargando...</div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center">
              <Package className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No se encontraron accesorios</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead className="hidden md:table-cell">Tipo</TableHead>
                    <TableHead className="hidden lg:table-cell">Nº Serie</TableHead>
                    <TableHead className="hidden lg:table-cell">Precio</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map(item => (
                    <TableRow
                      key={item.id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => navigate(`/accesorios/${item.id}`)}
                      data-testid={`accesorio-row-${item.id}`}
                    >
                      <TableCell>
                        <span className="font-mono font-semibold text-orange-600">{item.codigo_interno}</span>
                      </TableCell>
                      <TableCell className="font-medium">{item.nombre}</TableCell>
                      <TableCell className="hidden md:table-cell text-slate-500">{item.tipo || '—'}</TableCell>
                      <TableCell className="hidden lg:table-cell text-slate-500 font-mono text-xs">{item.numero_serie || '—'}</TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {item.precio_compra != null ? `${Number(item.precio_compra).toFixed(2)} €` : '—'}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${ESTADO_COLORS[item.estado] || 'bg-slate-100 text-slate-800'} text-xs`}>
                          {ESTADO_LABELS[item.estado] || item.estado}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/accesorios/${item.id}`)}
                            data-testid={`view-accesorio-${item.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => setDeleteId(item.id)}
                              data-testid={`delete-accesorio-${item.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Dialog */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuevo Accesorio</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-slate-500 bg-slate-50 rounded p-2">
              El código interno se generará automáticamente (ACC-XXXXXX)
            </p>
            <div>
              <Label>Nombre *</Label>
              <Input
                value={newItem.nombre}
                onChange={e => setNewItem({ ...newItem, nombre: e.target.value })}
                placeholder="Ej: Candado U-Lock"
                data-testid="new-accesorio-nombre"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo</Label>
                <Input
                  value={newItem.tipo}
                  onChange={e => setNewItem({ ...newItem, tipo: e.target.value })}
                  placeholder="Ej: Candado, Luz..."
                  data-testid="new-accesorio-tipo"
                />
              </div>
              <div>
                <Label>Nº Serie</Label>
                <Input
                  value={newItem.numero_serie}
                  onChange={e => setNewItem({ ...newItem, numero_serie: e.target.value })}
                  data-testid="new-accesorio-serie"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nº Factura</Label>
                <Input
                  value={newItem.numero_factura}
                  onChange={e => setNewItem({ ...newItem, numero_factura: e.target.value })}
                  data-testid="new-accesorio-factura"
                />
              </div>
              <div>
                <Label>Fecha Compra</Label>
                <Input
                  type="date"
                  value={newItem.fecha_compra}
                  onChange={e => setNewItem({ ...newItem, fecha_compra: e.target.value })}
                  data-testid="new-accesorio-fecha"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Año Modelo</Label>
                <Input
                  type="number"
                  value={newItem.año_modelo}
                  onChange={e => setNewItem({ ...newItem, año_modelo: e.target.value })}
                  placeholder="2025"
                  data-testid="new-accesorio-year"
                />
              </div>
              <div>
                <Label>Precio Compra (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newItem.precio_compra}
                  onChange={e => setNewItem({ ...newItem, precio_compra: e.target.value })}
                  data-testid="new-accesorio-precio"
                />
              </div>
            </div>
            <div>
              <Label>Estado</Label>
              <Select
                value={newItem.estado}
                onValueChange={v => setNewItem({ ...newItem, estado: v })}
              >
                <SelectTrigger data-testid="new-accesorio-estado">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="activo">Activo</SelectItem>
                  <SelectItem value="vendido">Vendido</SelectItem>
                  <SelectItem value="reparacion">En reparación</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notas</Label>
              <textarea
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 min-h-[60px]"
                value={newItem.notas}
                onChange={e => setNewItem({ ...newItem, notas: e.target.value })}
                data-testid="new-accesorio-notas"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddOpen(false)}>Cancelar</Button>
            <Button
              onClick={handleCreate}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="confirm-create-accesorio"
            >
              Crear Accesorio
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar Accesorio</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">¿Estás seguro de que deseas eliminar este accesorio? Esta acción no se puede deshacer.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>Cancelar</Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              data-testid="confirm-delete-accesorio"
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
